import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, CheckIcon, Loader2Icon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { GoldMedal } from './LifetimeSeal';
import { money } from './QuantityStepper';
import { SHIPPING_ESTIMATE_NOTE } from '../data/upsellFlows';
import type { UpsellStep, OrderSummary } from '../data/upsellFlows';
import type { ActiveConfirmation } from '../hooks/useUpsellFlow';
interface UpsellModalProps {
  step: UpsellStep | null;
  /** Shown after an accepted offer that confirms before checkout. */
  confirmation?: ActiveConfirmation | null;
  onContinue?: () => void;
  onFollowUp?: () => void;
  /** True while the customer is being routed to the Shopify cart. */
  routing: boolean;
  /** The chosen product has no Shopify link yet. */
  missingLink?: boolean;
  onAccept: () => void;
  onDecline: () => void;
  /** Close without choosing: stays on the page, nothing is added. */
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;

/** Coverage and shipping status for one side of the comparison. */
function OrderStatus({
  summary,
  highlight = false
}: {
  summary: OrderSummary;
  highlight?: boolean;
}) {
  return <ul className="mt-2 grid gap-1 text-[11px] font-bold uppercase leading-tight tracking-wide" data-id="element-994" data-mp="8mkc8dlnty5g">
      <li className={`flex items-start gap-1.5 ${highlight ? 'text-[#ffe79a]' : 'text-steel-300'}`} data-id="element-995" data-mp="djkj2qqc34xm">
        <ShieldCheckIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-996" data-mp="3c3aovbbqz9g4" />
        {summary.coverage}
      </li>
      <li className={`flex items-start gap-1.5 ${summary.freeShipping ? highlight ? 'text-grit-500' : 'text-steel-300' : 'text-steel-500'}`} data-id="element-997" data-mp="3h1lvzeanr5un">
        <TruckIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-998" data-mp="3qrxcs1ffx9iy" />
        {summary.freeShipping ? 'Free standard shipping' : 'Shipping at checkout · typ. ~$6.95*'}
      </li>
    </ul>;
}

/**
 * One negotiation step. Always shows what the customer has now, what the offer
 * is, the exact extra cost and the new total, with a visible way to say no.
 */
export function UpsellModal({
  step,
  confirmation = null,
  onContinue,
  onFollowUp,
  routing,
  missingLink,
  onAccept,
  onDecline,
  onClose
}: UpsellModalProps) {
  const acceptRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(step || confirmation);
  useEffect(() => {
    if (!open) return;
    acceptRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !routing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, routing, onClose]);
  return <AnimatePresence data-id="element-999" data-mp="2ca47f4crqact">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/85 backdrop-blur-sm sm:items-center sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.16,
      ease
    }} role="dialog" aria-modal="true" aria-labelledby="upsell-heading" data-id="element-1000" data-mp="2c92hxuv09lbk">
          <motion.div key={step ? step.id : 'confirmation'} className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto border-2 gold-edge bg-ink-950 shadow-[0_0_48px_rgba(242,201,76,0.14)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-1001" data-mp="1ei5y8b0dln3x">
            <div className="gold-plate flex items-center justify-between gap-3 px-5 py-2.5" data-id="element-1002" data-mp="1jgh5cdzadjig">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink-950" data-id="element-1003" data-mp="1oesh4u95643b">
                {step ? step.plate : 'Offer claimed'}
              </p>
              <button type="button" onClick={onClose} disabled={routing} aria-label="Close" className="-mr-2 flex h-8 w-8 items-center justify-center text-ink-950 transition-opacity duration-150 ease-grit hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-950" data-id="element-1004" data-mp="2c2jum16f4n66">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1005" data-mp="j93xls795q72" />
              </button>
            </div>

            {!step && confirmation && <div className="p-5 sm:p-7" data-id="element-1006" data-mp="1b0jinht5v4v6">
                <div className="flex items-center gap-4" data-id="element-1007" data-mp="1azi3b1543w7n">
                  <GoldMedal size={72} data-id="element-1008" data-mp="16xwovbcxj9vc" />
                  <h2 id="upsell-heading" className="grit-headline text-[clamp(1.85rem,6.5vw,2.75rem)] font-extrabold uppercase leading-[0.95] gold-text" data-id="element-1009" data-mp="16z81m0ocrryl">
                    {confirmation.headline}
                  </h2>
                </div>
                <p className="mt-4 text-[15px] font-semibold leading-relaxed text-steel-200" data-id="element-1010" data-mp="1upbrz6cj5v7n">{confirmation.body}</p>
                {missingLink && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-1011" data-mp="1x8ymklpk85ls">
                    This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
                  </p>}
                <button ref={acceptRef} type="button" onClick={onContinue} disabled={routing} className="mt-6 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-1012" data-mp="2b8pxueooz7w3">
                  {routing ? <>
                      <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1013" data-mp="33xx6w7lrvfvl" />
                      Taking you to checkout
                    </> : <>
                      {confirmation.cta}
                      <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1014" data-mp="zr7qmhlba3ww" />
                    </>}
                </button>
                {confirmation.followUp && <button type="button" onClick={onFollowUp} disabled={routing} className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-center text-[13px] font-bold uppercase tracking-wide text-steel-300 underline-offset-4 transition-colors duration-150 ease-grit hover:text-grit-500 hover:underline disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1015" data-mp="18rmglu98076q">
                    {confirmation.followUp.label}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1016" data-mp="3tcmx3le23gbf" />
                  </button>}
                <p className="mt-4 text-center text-[11px] text-steel-500" data-id="element-1017" data-mp="28u2nk43lth3i">Taxes, if applicable, calculated at checkout.</p>
              </div>}

            {step && <div className="p-5 sm:p-7" data-id="element-1018" data-mp="20066v4t7kwvh">
              <h2 id="upsell-heading" className="grit-headline text-[clamp(1.85rem,6.5vw,3rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-1019" data-mp="1zzejuidog7gt">
                {step.headline}
                <br data-id="element-1020" data-mp="1v2nsv00c3rgm" />
                <span className="gold-text" data-id="element-1021" data-mp="1v35vcedlb1wz">{step.highlight}</span>
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-steel-200" data-id="element-1022" data-mp="1h34xz8wjacm9">{step.intro}</p>

              {/* Current vs. new, side by side even on phones: totals are the first thing read. */}
              <div className="mt-5 grid grid-cols-2 gap-2 sm:gap-3" data-id="element-1023" data-mp="3bfuodgnp13ow">
                <div className="border border-white/12 bg-white/[0.03] p-3 sm:p-4" data-id="element-1024" data-mp="3bejklgu8zvxs">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1025" data-mp="3ffvfa0rzb7cu">Your selection</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-1026" data-mp="1y9jde44ovbxo">
                    {step.current.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-400" data-id="element-1027" data-mp="1tgqc5q8filqc">{step.current.detail}</p>
                  <OrderStatus summary={step.current} data-id="element-1028" data-mp="1ohvr0hmzol32" />
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1029" data-mp="1kiwfufanzs36">
                    Current product total
                  </p>
                  <p className="font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-1030" data-mp="1ajfajyzbrg7q">
                    {money(step.current.total)}
                  </p>
                </div>
                <div className="border-2 border-grit-500 bg-grit-500/[0.07] p-3 sm:p-4" data-id="element-1031" data-mp="34xq0karnogf3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-1032" data-mp="2zyvixc5fcxwk">Upgrade</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-1033" data-mp="38g5ln9it4yaq">
                    {step.upgrade.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-300" data-id="element-1034" data-mp="3cf4qku4jgv26">{step.upgrade.detail}</p>
                  <OrderStatus summary={step.upgrade} highlight data-id="element-1035" data-mp="3m7aoslldw8pt" />
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-1036" data-mp="3m6itzczm2f0l">
                    New product total
                  </p>
                  <p className="font-display text-3xl font-extrabold leading-none text-grit-500" data-id="element-1037" data-mp="2thv4t6bqr5hr">
                    {money(step.upgrade.total)}
                  </p>
                </div>
              </div>
              <p className="mt-2 bg-grit-500 px-4 py-2 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950" data-id="element-1038" data-mp="33huamvgs0hqx">
                {step.extra}
              </p>

              {step.economics && <dl className="mt-3 grid gap-1 border border-white/10 px-4 py-3 text-[13px]" data-id="element-1039" data-mp="ioqvrguvsyty">
                  <div className="flex justify-between gap-3 text-steel-300" data-id="element-1040" data-mp="1zvifzwqx7z2q">
                    <dt data-id="element-1041" data-mp="24u3b5hrbfskb">Product price difference</dt>
                    <dd className="font-semibold text-offwhite" data-id="element-1042" data-mp="29ltm9py2ukcs">{step.economics.productDiff}</dd>
                  </div>
                  <div className="flex justify-between gap-3 text-steel-300" data-id="element-1043" data-mp="2dnyeqlcshaxu">
                    <dt data-id="element-1044" data-mp="1gx5xhke4dcuh">Estimated shipping you no longer pay</dt>
                    <dd className="font-semibold text-offwhite" data-id="element-1045" data-mp="1gyqurhrtj9w1">{step.economics.shippingSaved}</dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-white/10 pt-1.5 font-bold uppercase tracking-wide text-grit-500" data-id="element-1046" data-mp="1ly3ommh3xh84">
                    <dt data-id="element-1047" data-mp="1ux77bpcd3um3">Expected difference, before tax</dt>
                    <dd data-id="element-1048" data-mp="1uukt56uyuguw">{step.economics.result}</dd>
                  </div>
                </dl>}

              {step.valueLine && <dl className="mt-4 grid grid-cols-3 border border-white/10 text-center" data-id="element-1049" data-mp="36b2pz82h6li2">
                  <div className="p-2.5" data-id="element-1050" data-mp="3ggaqih80kfxd">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1051" data-mp="3gi58bmnznrx8">Normal value</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-steel-400 line-through" data-id="element-1052" data-mp="36q7xih0w71z1">
                      {step.valueLine.normal}
                    </dd>
                  </div>
                  <div className="border-x border-white/10 p-2.5" data-id="element-1053" data-mp="2xpjhxhvuotk1">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-1054" data-mp="2xr49cymvbwdo">
                      {step.valueLine.todayLabel}
                    </dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-grit-500" data-id="element-1055" data-mp="2nses982sqfyh">{step.valueLine.today}</dd>
                  </div>
                  <div className="p-2.5" data-id="element-1056" data-mp="2jruymehfluh3">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1057" data-mp="m0ifjv4w7024">Free shipping</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-offwhite" data-id="element-1058" data-mp="hzger9vbufy1">{step.valueLine.shipping}</dd>
                  </div>
                </dl>}

              <div className="mt-4 flex items-start gap-4" data-id="element-1059" data-mp="2g8e0x6tm8i1m">
                {step.lifetimeMedal && <div className="hidden shrink-0 sm:block" data-id="element-1060" data-mp="22h8vfo3kgvjd">
                    <GoldMedal size={78} data-id="element-1061" data-mp="22emp1rsewm2q" />
                  </div>}
                <div data-id="element-1062" data-mp="26iazqjccbxs7">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1063" data-mp="2u7m0w1xlru6v">You get</p>
                  <ul className="mt-2 grid gap-1.5 text-[14px] font-semibold text-steel-200" data-id="element-1064" data-mp="2z6gix1nqxepx">
                    {step.includes.map(line => <li key={line} className="flex items-start gap-2" data-id="element-1065" data-mp="2l6fghhru1z8c">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1066" data-mp="2q58vxfqok0b5" />
                        {line}
                      </li>)}
                  </ul>
                </div>
              </div>

              <p className="mt-4 border-l-2 border-grit-500 pl-3 text-[13px] font-bold uppercase tracking-wide text-offwhite" data-id="element-1067" data-mp="1eosczcfx31jj">
                {step.clarification}
              </p>

              {missingLink && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-1068" data-mp="1g80o9t7f3nfd">
                  This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
                </p>}

              <button ref={acceptRef} type="button" onClick={onAccept} disabled={routing} className="mt-5 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-1069" data-mp="nh0k9i936jnp">
                {routing ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1070" data-mp="346w5jrqmteqw" />
                    Taking you to checkout
                  </> : <>
                    {step.accept}
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1071" data-mp="3czg996ls69il" />
                  </>}
              </button>
              <button type="button" onClick={onDecline} disabled={routing} className="mt-2.5 w-full border border-white/20 px-4 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-steel-300 transition-colors duration-150 ease-grit hover:border-white/40 hover:text-offwhite disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1072" data-mp="3hy19h6118t71">
                {step.decline}
              </button>
              <p className="mt-3 text-center text-[11px] leading-snug text-steel-500" data-id="element-1073" data-mp="21m40chb5z0pk">
                {step.economics || !step.current.freeShipping ? SHIPPING_ESTIMATE_NOTE : 'Taxes, if applicable, calculated at checkout.'}
              </p>
            </div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}